import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Copy, Trash2, Shield } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { CHIP_DENSE } from '../../../utils/chipColors';
import { isWhiteLabel } from '../../../config/tenantConfig';
import Dropdown from '../../ui/Dropdown';
import HelpTip from '../../ui/HelpTip';
import { Leg, StopLossConfig } from '../../../types/strategy';
import {
  SELECTION_METHOD_OPTIONS,
  PREMIUM_OPERATOR_OPTIONS,
  STRADDLE_WIDTH_OPTIONS,
  COUNT_OPTIONS,
  UNDERLYING_PCT,
} from '../../../utils/strategy/strategyConstants';
import {
  generateStrikeOptions,
  generatePercentageOptions,
  generateStraddlePremiumPercentageOptions,
} from '../../../utils/strategy/strikeOptionsGenerator';
import { isValidStopLoss, isValidTargetProfit } from '../../../utils/strategy/riskValidators';
import { mainRecycleEligible } from '../../../utils/strategy/positionHelpers';
import {
  RE_ENTRY_TYPE, RE_EXECUTE_TYPE, PREMIUM_OPERATOR,
  RISK_MANAGEMENT_TYPE, TARGET_PROFIT_TYPE, TSL_TYPE, WAT_TYPE,
  UL_WAT_TYPES, UL_TSL_TYPES, UL_RISK_TYPES,
} from '../../../constants/tradingConstants';
import { STRATEGY_HELP, HelpEntry } from '../../../utils/strategy/strategyHelpText';
import TrailingStopLossDialog from './TrailingStopLossDialog';

// Percentage-family TP types (premium + UL). Drives the Num input's max/step
// so UL_PERCENTAGE gets the same 1000% ceiling + step-of-1 as premium.
// Widened per Phase 6 review-fix H7. Points-family types have no cap.
const PERCENT_TP_TYPES: readonly string[] = [
  TARGET_PROFIT_TYPE.PERCENTAGE,
  TARGET_PROFIT_TYPE.UL_PERCENTAGE,
];

// Re-entry flavours carry descriptive labels (parity with ReEntryControl, not the terse RE_ENTRY_TYPE_OPTIONS).
// Inside the SL-Re-Entry / TP-Re-Exec columns the SL/TP prefix is redundant —
// the column header (and the ADV_COLS toggle chip above it) already carry
// that context. Strip the prefix from dropdown options + cell inline labels
// for visual density; the chip + column header keep the long form.
const RE_ENTRY_OPTS = [
  { value: RE_ENTRY_TYPE.SL_REENTRY, label: 'Re-Entry' },
  { value: RE_ENTRY_TYPE.SL_RECOST, label: 'Re-Cost' },
  { value: RE_ENTRY_TYPE.SL_REEXEC, label: 'Re-Exec' },
];
const RE_EXEC_OPTS = [{ value: RE_EXECUTE_TYPE.TP_REEXEC, label: 'Re-Exec' }];

// Per-cell type-option overrides — show context in the dropdown ("SL %"
// instead of generic "Percentage") so users can scan which cell's type is
// configured without reading the column header. Enum VALUES are unchanged
// (POINTS / PERCENTAGE / RANGE); only display labels differ. The shared
// RISK_MANAGEMENT_TYPE_OPTIONS / TARGET_PROFIT_TYPE_OPTIONS constants are
// left untouched so other consumers (e.g. StrategyConfiguration's
// strategy-level MTM panel) keep neutral "Points"/"Percentage" labels.
// Phase 6 — UL variants share the underlying-spot anchor (not premium).
// Rendered inline with the premium types; the backend rejects UL values with
// a clear error when UL_MONITOR_ENABLED=false (the frontend has no
// build-time knowledge of the flag), and the frontend cross-config validator
// mirrors the backend UL rules (validateUlCrossConfig).
const SL_TYPE_OPTS_BASE = [
  { value: RISK_MANAGEMENT_TYPE.POINTS,        label: 'SL Pt' },
  { value: RISK_MANAGEMENT_TYPE.PERCENTAGE,    label: 'SL %' },
  { value: RISK_MANAGEMENT_TYPE.RANGE,         label: 'SL Range' },
  { value: RISK_MANAGEMENT_TYPE.UL_POINTS,     label: 'SL UL Pt' },
  { value: RISK_MANAGEMENT_TYPE.UL_PERCENTAGE, label: 'SL UL %' },
];
const TP_TYPE_OPTS = [
  { value: TARGET_PROFIT_TYPE.POINTS,        label: 'TP Pt' },
  { value: TARGET_PROFIT_TYPE.PERCENTAGE,    label: 'TP %' },
  { value: TARGET_PROFIT_TYPE.UL_POINTS,     label: 'TP UL Pt' },
  { value: TARGET_PROFIT_TYPE.UL_PERCENTAGE, label: 'TP UL %' },
];
const TSL_TYPE_OPTS = [
  { value: TSL_TYPE.POINTS,        label: 'TSL Pt' },
  { value: TSL_TYPE.PERCENTAGE,    label: 'TSL %' },
  { value: TSL_TYPE.UL_POINTS,     label: 'TSL UL Pt' },
  { value: TSL_TYPE.UL_PERCENTAGE, label: 'TSL UL %' },
];
// UL W&T carries an explicit direction: spot can breach the reference either
// way, and the wait fires only on the chosen side. Labels use ↑/↓ to keep the
// dropdown scannable in the same width the premium options occupy.
const WAT_TYPE_OPTS = [
  { value: WAT_TYPE.POINTS,         label: 'W&T Pt' },
  { value: WAT_TYPE.PERCENTAGE,     label: 'W&T %' },
  { value: WAT_TYPE.UL_POINTS_UP,   label: 'UL ↑ Pt' },
  { value: WAT_TYPE.UL_POINTS_DOWN, label: 'UL ↓ Pt' },
  { value: WAT_TYPE.UL_PCT_UP,      label: 'UL ↑ %' },
  { value: WAT_TYPE.UL_PCT_DOWN,    label: 'UL ↓ %' },
];

const reEntryHelp = (type: string) =>
  type === RE_ENTRY_TYPE.SL_REEXEC ? STRATEGY_HELP.reExec
  : type === RE_ENTRY_TYPE.SL_RECOST ? STRATEGY_HELP.reCost
  : STRATEGY_HELP.reEntry;

const wl = isWhiteLabel();
const FOCUS = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
const BRAND_BG = wl ? 'bg-zebu-600' : 'bg-blue-600';
const BRAND_BORDER = wl ? 'border-zebu-600' : 'border-blue-600';
const BRAND_CHIP_ON = wl
  ? 'bg-zebu-600 text-white border-zebu-600'
  : 'bg-blue-600 text-white border-blue-600';

const numCls = (w: string) =>
  cn(
    `text-xs text-center border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 ${FOCUS} disabled:opacity-40`,
    w,
  );

const Num: React.FC<{
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  width?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, width = 'w-14', min = 0, max, step = 0.1, disabled }) => {
  // Local text state lets the field hold an empty/intermediate value while
  // typing (e.g. clearing "8" to type "9") instead of snapping back to a
  // coerced number on every keystroke.
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : '');

  // Re-sync only on an *external* value change — when our own onChange echoes
  // the same number back, keep the raw text so partial entries like "0." survive.
  useEffect(() => {
    if (parseFloat(text) !== value) setText(Number.isFinite(value) ? String(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={text}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parseFloat(e.target.value);
        if (Number.isNaN(parsed)) return; // empty/intermediate — don't push yet
        const clamped = max != null ? Math.min(parsed, max) : parsed;
        onChange(Math.max(min, clamped));
      }}
      onBlur={() => {
        // Empty/invalid on blur → restore the last committed value.
        if (Number.isNaN(parseFloat(text))) setText(Number.isFinite(value) ? String(value) : '');
      }}
      className={numCls(width)}
    />
  );
};

// Numeric input for PERCENTAGE_OF_UNDERLYING.
//   - step={0.01} so HTML5 doesn't flag valid 2-decimal outputs as :invalid
//     (blur can produce 0.06 from 0.055; step=0.05 would render that :invalid).
//   - No on-change clamping: keeps the parent value in sync with what the user
//     is typing; canonicalization happens on blur / Enter. `lastSent` ref
//     distinguishes our own onChange echoes from a true external value change,
//     so useEffect doesn't erase in-flight typing.
//   - Enter → blur so form-level submits normalize before validators run.
//   - onBlur: round to 2 decimals, clamp to [0.05, 10], restore default 1 if empty.
const UnderlyingPctInput: React.FC<{
  value: number;
  onChange: (v: number) => void;
}> = ({ value, onChange }) => {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : '');
  const lastSent = useRef<number | null>(value);
  useEffect(() => {
    if (lastSent.current === value) return;
    setText(Number.isFinite(value) ? String(value) : '');
    lastSent.current = value;
  }, [value]);
  const normalize = (raw: string): number => {
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) return UNDERLYING_PCT.DEFAULT; // empty / NaN → default
    const scale = 10 ** UNDERLYING_PCT.DECIMALS;
    const rounded = Math.round(parsed * scale) / scale;
    return Math.min(UNDERLYING_PCT.MAX, Math.max(UNDERLYING_PCT.MIN, rounded));
  };
  const emit = (v: number) => {
    lastSent.current = v;
    onChange(v);
  };
  return (
    <input
      type="number"
      min={UNDERLYING_PCT.MIN}
      max={UNDERLYING_PCT.MAX}
      step={0.01}
      value={text}
      placeholder="Pct%"
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parseFloat(e.target.value);
        if (Number.isFinite(parsed)) emit(parsed);
      }}
      onBlur={() => {
        const v = normalize(text);
        setText(String(v));
        emit(v);
      }}
      onKeyDown={(e) => {
        // Enter must canonicalize before form-level submit sees the value.
        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
      }}
      className={numCls('w-16')}
    />
  );
};

// muted "+" affordance shown in an off cell — keeps the column aligned
const AddCell: React.FC<{ label: string; onClick: () => void; disabled?: boolean; title?: string }> = ({
  label,
  onClick,
  disabled,
  title,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'inline-flex items-center gap-0.5 text-xs font-medium transition-colors whitespace-nowrap',
      disabled
        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
        : 'text-sky-500 dark:text-sky-400 hover:text-sky-600',
    )}
  >
    <Plus className="h-3 w-3" />
    {label}
  </button>
);

const RemoveBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="shrink-0 p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors"
    title="Disable"
  >
    <X className="h-3 w-3" />
  </button>
);

// Labelled field wrapper for the mobile (<lg) stacked-card layout.
const MobileField: React.FC<{ label: string; entry?: HelpEntry; children: React.ReactNode }> = ({ label, entry, children }) => (
  <div>
    <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">
      {label}
      {entry && <HelpTip entry={entry} />}
    </div>
    {children}
  </div>
);

// Desktop column-header cell, with an optional HelpTip beside the label.
const HCOL_CLS = 'shrink-0 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const HCol: React.FC<{ w: string; entry?: HelpEntry; align?: 'right'; children: React.ReactNode }> = ({ w, entry, align, children }) => (
  <span className={cn(HCOL_CLS, w, 'flex items-center gap-1', align === 'right' && 'justify-end')}>
    {children}
    {entry && <HelpTip entry={entry} />}
  </span>
);

// ── column registry ───────────────────────────────────────────────────────────
type AdvKey = 'trailSL' | 'wat' | 'reEntry' | 'reExec';
const ADV_COLS: { key: AdvKey; label: string }[] = [
  { key: 'trailSL', label: 'Trail SL' },
  { key: 'wat', label: 'Wait & Trade' },
  { key: 'reEntry', label: 'SL Re-Entry' },
  { key: 'reExec', label: 'TP Re-Exec' },
];

interface LegMatrixProps {
  legs: Leg[];
  onUpdateLeg: (index: number, patch: Partial<Leg>) => void;
  onAddLeg?: () => void;
  onAddHedge?: (index: number) => void;
  onRemoveLeg?: (index: number) => void;
  onCopyLeg?: (index: number) => void;
  isOrbEnabled?: boolean;
  isOvernight?: boolean;
  disabled?: boolean;
}

const LegMatrix: React.FC<LegMatrixProps> = ({
  legs,
  onUpdateLeg,
  onAddLeg,
  onAddHedge,
  onRemoveLeg,
  onCopyLeg,
  isOrbEnabled = false,
  isOvernight = false,
}) => {
  // A main leg can carry at most one hedge; the "Add hedge" affordance hides
  // once its hedge exists. Keyed by the main leg's id.
  const mainHasHedge = (mainId: string) =>
    legs.some((l) => l.isHedge && l.hedgeForLegId === mainId);
  // Sequential 1-based display number for MAIN legs only (hedges render as ↳),
  // so interleaved hedges don't make mains read 1, 3, 5…. The "protects L#"
  // label references the target main's sequential number, not its array index.
  const mainNoById = new Map<string, number>();
  let _mainSeq = 0;
  legs.forEach((l) => {
    if (!l.isHedge) mainNoById.set(l.id, (_mainSeq += 1));
  });
  // At least one main leg must remain — a main's delete is gated on this; a
  // hedge is always removable (removing it never drops the last main).
  const mainCount = _mainSeq;
  // Display label of a hedge's target main leg. Falls back to '?' if the link is
  // dangling (validator flags it).
  const hedgeTargetLabel = (leg: Leg) => {
    const no = mainNoById.get(leg.hedgeForLegId ?? '');
    return no ? `L${no}` : '?';
  };
  // Default a feature column visible when ANY leg already has it enabled,
  // so editing a configured strategy reveals what's set.
  const [visible, setVisible] = useState<Record<AdvKey, boolean>>(() => ({
    trailSL: legs.some((l) => l.trailingStopLoss?.enabled),
    wat: legs.some((l) => l.waitAndTrade?.enabled),
    reEntry: legs.some((l) => l.reEntry?.enabled),
    reExec: legs.some((l) => l.reExecute?.enabled),
  }));
  const toggleCol = (k: AdvKey) => setVisible((p) => ({ ...p, [k]: !p[k] }));

  // Leg index whose Trailing Stop Loss is being entered via the guided dialog.
  const [tslDialogLeg, setTslDialogLeg] = useState<number | null>(null);

  const slTypeOpts = isOrbEnabled
    ? SL_TYPE_OPTS_BASE
    : SL_TYPE_OPTS_BASE.filter((o) => o.value !== 'RANGE');

  // ── per-cell renderers ──────────────────────────────────────────────────────
  const StrikeMethod = (leg: Leg, i: number) => (
    <Dropdown
      size="sm"
      portal
      value={leg.selectionMethod}
      options={SELECTION_METHOD_OPTIONS}
      onChange={(v) => {
        const newMethod = v as Leg['selectionMethod'];
        // Preserve stale method-specific fields across a switch-and-back (so the
        // user's PREMIUM value / straddle % / etc. survives a dropdown misclick).
        // `computeStrategySignature` normalizes non-current-method fields so
        // stale state doesn't cause phantom "unsaved changes".
        const next: Partial<Leg> = { selectionMethod: newMethod, strikePrice: 'ATM' };
        // Seed defaults ONLY if the leg has no prior value for this method
        // (guards against overwriting a previously-configured value on a
        // switch-and-back). The check reads the LEG's state, not `next`.
        if (newMethod === 'PERCENTAGE_OF_UNDERLYING') {
          // Seed each field independently — an orphaned leg (one field set,
          // the other undefined, e.g. from a partial import) preserves the
          // user's chosen field while filling the missing one with the default.
          if (!leg.underlyingPremiumOperator) next.underlyingPremiumOperator = PREMIUM_OPERATOR.CLOSEST;
          if (leg.underlyingPercentage === undefined) next.underlyingPercentage = UNDERLYING_PCT.DEFAULT;
        }
        onUpdateLeg(i, next);
      }}
    />
  );

  const StrikeValue = (leg: Leg, i: number) => {
    switch (leg.selectionMethod) {
      case 'ATM_PERCENT':
        return (
          <Dropdown size="sm" portal value={leg.strikePrice} options={generatePercentageOptions()}
            onChange={(v) => onUpdateLeg(i, { strikePrice: v })} />
        );
      case 'PREMIUM':
        return (
          <div className="flex items-center gap-1">
            <Dropdown size="sm" portal value={leg.premiumOperator || 'CLOSEST'} options={PREMIUM_OPERATOR_OPTIONS}
              onChange={(v) => onUpdateLeg(i, { premiumOperator: v as any })} />
            <Num width="w-16" value={leg.premiumValue || 0} placeholder="Prem"
              onChange={(v) => onUpdateLeg(i, { premiumValue: v })} />
          </div>
        );
      case 'PERCENTAGE_OF_STRADDLE_PREMIUM':
        return (
          <div className="flex items-center gap-1">
            <Dropdown size="sm" portal value={leg.straddlePremiumOperator || 'CLOSEST'} options={PREMIUM_OPERATOR_OPTIONS}
              onChange={(v) => onUpdateLeg(i, { straddlePremiumOperator: v as any })} />
            <Dropdown size="sm" portal value={(leg.straddlePremiumPercentage || 5).toString()}
              options={generateStraddlePremiumPercentageOptions()}
              onChange={(v) => onUpdateLeg(i, { straddlePremiumPercentage: parseInt(v) })} />
          </div>
        );
      case 'STRADDLE_WIDTH':
        return (
          <Dropdown size="sm" portal value={(leg.straddleWidth ?? 0).toString()} options={STRADDLE_WIDTH_OPTIONS}
            onChange={(v) => onUpdateLeg(i, { straddleWidth: parseFloat(v) })} />
        );
      case 'PERCENTAGE_OF_UNDERLYING':
        return (
          <div className="flex items-center gap-1">
            <Dropdown size="sm" portal value={leg.underlyingPremiumOperator || 'CLOSEST'} options={PREMIUM_OPERATOR_OPTIONS}
              onChange={(v) => onUpdateLeg(i, { underlyingPremiumOperator: v as any })} />
            <UnderlyingPctInput value={leg.underlyingPercentage ?? UNDERLYING_PCT.DEFAULT}
              onChange={(v) => onUpdateLeg(i, { underlyingPercentage: v })} />
          </div>
        );
      case 'ATM_POINTS':
      default:
        return (
          <Dropdown size="sm" portal value={leg.strikePrice} options={generateStrikeOptions()}
            onChange={(v) => onUpdateLeg(i, { strikePrice: v })} />
        );
    }
  };

  // Shared core controls — used by both the desktop table row and the mobile card.
  const OptionTypeToggle = (leg: Leg, i: number) => {
    const typeCls = leg.optionType === 'CE' ? 'bg-emerald-600 border-emerald-600' : 'bg-rose-600 border-rose-600';
    // A hedge mirrors its main leg's option type (a protective hedge for a CE
    // short is a CE, for a PE short a PE) — render static; it stays in sync via
    // the main→hedge option-type cascade in usePositionManagement.
    return leg.isHedge ? (
      <div className={cn('w-full px-2 py-1.5 text-xs font-bold rounded-md border text-white text-center', typeCls)}
        title="A hedge mirrors its main leg's option type">
        {leg.optionType}
      </div>
    ) : (
      <button type="button"
        onClick={() => onUpdateLeg(i, { optionType: leg.optionType === 'CE' ? 'PE' : 'CE' })}
        className={cn('w-full px-2 py-1.5 text-xs font-bold rounded-md border text-white', typeCls)}>
        {leg.optionType}
      </button>
    );
  };

  const ActionToggle = (leg: Leg, i: number) => (
    // A hedge is BUY-only — render a static, non-interactive BUY chip.
    leg.isHedge ? (
      <div className={cn('w-full px-2 py-1.5 text-xs font-bold rounded-md border text-white text-center', BRAND_BG, BRAND_BORDER)}
        title="A hedge leg is always BUY">
        BUY
      </div>
    ) : (
      <button type="button"
        onClick={() => onUpdateLeg(i, { actionType: leg.actionType === 'BUY' ? 'SELL' : 'BUY' })}
        className={cn('w-full px-2 py-1.5 text-xs font-bold rounded-md border text-white',
          leg.actionType === 'BUY' ? `${BRAND_BG} ${BRAND_BORDER}` : 'bg-orange-500 border-orange-500')}>
        {leg.actionType}
      </button>
    )
  );

  const LotsInput = (leg: Leg, i: number) => (
    <>
      {leg.isHedge ? (
        // A hedge mirrors its main leg's lots — render static; it stays in sync via
        // the main→hedge lots cascade in usePositionManagement.
        <div className="w-full px-2 py-1.5 text-xs font-bold rounded-md border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-center bg-gray-50 dark:bg-gray-700/50"
          title="A hedge mirrors its main leg's lots">
          {leg.totalLots}
        </div>
      ) : (
        <Num width="w-full" value={leg.totalLots} min={1} max={200} step={1}
          onChange={(v) => onUpdateLeg(i, { totalLots: Math.max(1, Math.round(v)) })} />
      )}
      {leg.totalLots > 100 && (
        <div className="mt-0.5 text-[9px] leading-tight text-center text-amber-600 dark:text-amber-400"
          title={`${leg.totalLots} lots will split into multiple broker orders per allocation — verify your margin.`}>
          splits
        </div>
      )}
    </>
  );

  // SL changes cascade to dependent controls (mirrors StopLossControl).
  const applySL = (leg: Leg, i: number, next: StopLossConfig) => {
    const valid = isValidStopLoss(next);
    onUpdateLeg(i, {
      stopLoss: next,
      ...(valid
        ? {}
        : {
            trailingStopLoss: { ...leg.trailingStopLoss, enabled: false },
            reEntry: { ...leg.reEntry, enabled: false },
          }),
    });
  };

  const SLCell = (leg: Leg, i: number) => {
    const sl = leg.stopLoss;
    if (!sl?.enabled) {
      return <AddCell label="SL" onClick={() => applySL(leg, i, { ...sl, enabled: true })} />;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-red-500 dark:text-red-400">
            SL
            <HelpTip entry={STRATEGY_HELP.stopLoss} />
          </span>
          <RemoveBtn onClick={() => applySL(leg, i, { ...sl, enabled: false })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={sl.type} options={slTypeOpts}
          onChange={(v) =>
            applySL(leg, i, {
              ...sl,
              type: v as any,
              value: 0,
              rangeSide: v === 'RANGE' ? (leg.actionType?.toUpperCase() === 'SELL' ? 'HIGH' : 'LOW') : undefined,
            })
          } />
        {sl.type === 'RANGE' ? (
          <Dropdown size="sm" portal className="w-full"
            value={sl.rangeSide || (leg.actionType?.toUpperCase() === 'SELL' ? 'HIGH' : 'LOW')}
            options={leg.actionType?.toUpperCase() === 'SELL'
              ? [{ value: 'HIGH', label: 'Range High' }]
              : [{ value: 'LOW', label: 'Range Low' }]}
            onChange={(v) => onUpdateLeg(i, { stopLoss: { ...sl, rangeSide: v as 'HIGH' | 'LOW' } })} />
        ) : (
          <Num width="w-full" value={sl.value} placeholder="0" onChange={(v) => applySL(leg, i, { ...sl, value: v })} />
        )}
        {isOvernight && (
          <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={sl.reinstateOvernight !== false}
                onChange={(e) => onUpdateLeg(i, { stopLoss: { ...sl, reinstateOvernight: e.target.checked } })} />
              Overnight
            </label>
            <HelpTip entry={STRATEGY_HELP.reinstateOvernight} />
          </div>
        )}
      </div>
    );
  };

  const TargetCell = (leg: Leg, i: number) => {
    const tp = leg.targetProfit;
    if (!tp?.enabled) {
      return <AddCell label="Target" onClick={() => onUpdateLeg(i, { targetProfit: { ...tp, enabled: true } })} />;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-blue-600 dark:text-blue-400">
            TP
            <HelpTip entry={STRATEGY_HELP.targetProfit} />
          </span>
          <RemoveBtn onClick={() => onUpdateLeg(i, { targetProfit: { ...tp, enabled: false }, reExecute: { ...leg.reExecute, enabled: false } })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={tp.type} options={TP_TYPE_OPTS}
          onChange={(v) => onUpdateLeg(i, { targetProfit: { ...tp, type: v as any, value: 0 } })} />
        <Num width="w-full" value={tp.value} placeholder="0" max={PERCENT_TP_TYPES.includes(tp.type) ? 1000 : undefined}
          step={PERCENT_TP_TYPES.includes(tp.type) ? 1 : 0.05}
          onChange={(v) => onUpdateLeg(i, { targetProfit: { ...tp, value: v } })} />
      </div>
    );
  };

  const TrailCell = (leg: Leg, i: number) => {
    const tsl = leg.trailingStopLoss;
    const slValid = isValidStopLoss(leg.stopLoss);
    if (!tsl?.enabled) {
      // Re-enabling a previously-disabled UL TSL: skip the guided dialog and
      // turn the config back on inline with its saved values; users edit UL
      // fields via the inline editor once enabled. The dialog itself offers
      // units matching the leg's SL family (premium vs UL).
      const preExistingUlTsl = !!(tsl && UL_TSL_TYPES.includes(tsl.type));
      return <AddCell label="Trail" disabled={!slValid} title={!slValid ? 'Requires a valid Stop Loss' : undefined}
        onClick={() => {
          if (preExistingUlTsl) {
            onUpdateLeg(i, { trailingStopLoss: { ...tsl!, enabled: true } });
          } else {
            setTslDialogLeg(i);
          }
        }} />;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-amber-600 dark:text-amber-400">
            TSL
            <HelpTip entry={STRATEGY_HELP.trailingStopLoss} />
          </span>
          <RemoveBtn onClick={() => onUpdateLeg(i, { trailingStopLoss: { ...tsl, enabled: false } })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={tsl.type} options={TSL_TYPE_OPTS}
          onChange={(v) => onUpdateLeg(i, { trailingStopLoss: { ...tsl, type: v as any, instrumentMoveValue: 0, stopLossMoveValue: 0 } })} />
        <div className="grid grid-cols-2 gap-1">
          <Num width="w-full" value={tsl.instrumentMoveValue} placeholder="Instr"
            onChange={(v) => onUpdateLeg(i, { trailingStopLoss: { ...tsl, instrumentMoveValue: v } })} />
          <Num width="w-full" value={tsl.stopLossMoveValue} placeholder="SL"
            onChange={(v) => onUpdateLeg(i, { trailingStopLoss: { ...tsl, stopLossMoveValue: v } })} />
        </div>
      </div>
    );
  };

  const WatCell = (leg: Leg, i: number) => {
    const wt = leg.waitAndTrade;
    if (!wt?.enabled) {
      return <AddCell label="W&T" onClick={() => onUpdateLeg(i, { waitAndTrade: { ...wt, enabled: true } })} />;
    }
    const isUlWat = UL_WAT_TYPES.includes(wt.type);
    // ORB gates only the PREMIUM W&T retry loop (the retry loop and ORB
    // scan share the fill trigger); UL W&T rests no broker order and its
    // spot monitor is independent, so ORB-enabled strategies still let a
    // UL W&T carry over. Fix per Phase 6 review M2.
    const overnightDisabled = isOrbEnabled && !isUlWat;
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-purple-600 dark:text-purple-400">
            W&T
            <HelpTip entry={STRATEGY_HELP.waitAndTrade} />
          </span>
          <RemoveBtn onClick={() => onUpdateLeg(i, { waitAndTrade: { ...wt, enabled: false } })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={wt.type} options={WAT_TYPE_OPTS}
          onChange={(v) => {
            const nextType = v as any;
            const nextIsUl = UL_WAT_TYPES.includes(nextType);
            onUpdateLeg(i, { waitAndTrade: {
              ...wt,
              type: nextType,
              value: 0,
              // `tradeOnlyFirstEntry` is UL-W&T-only. When the user switches
              // to a premium W&T type, the checkbox is hidden but a prior
              // truthy value would still ship to backend on save (per Phase 6
              // review-fix H2). Clear it here so hidden state can't persist.
              tradeOnlyFirstEntry: nextIsUl ? (wt.tradeOnlyFirstEntry || false) : false,
            }})
          }} />
        <Num width="w-full" value={wt.value} placeholder="0"
          onChange={(v) => onUpdateLeg(i, { waitAndTrade: { ...wt, value: v } })} />
        {isOvernight && (
          <div className={cn('flex items-center gap-1 text-[10px]', overnightDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400')}>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={!!wt.continueOvernight} disabled={overnightDisabled}
                onChange={(e) => onUpdateLeg(i, { waitAndTrade: { ...wt, continueOvernight: e.target.checked } })} />
              Overnight
            </label>
            <HelpTip entry={overnightDisabled ? STRATEGY_HELP.continueOvernightOrbDisabled : STRATEGY_HELP.continueOvernight} />
          </div>
        )}
        {/* Phase 6 — UL W&T only. When ON, re-entries enter directly on
            cross-back; when OFF (default), a fresh UL_MONITOR_PENDING row
            is written per re-entry and the spot wait re-runs. Hidden on
            premium W&T (ignored server-side there but the checkbox would
            confuse). */}
        {isUlWat && (
          <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={!!wt.tradeOnlyFirstEntry}
                onChange={(e) => onUpdateLeg(i, { waitAndTrade: { ...wt, tradeOnlyFirstEntry: e.target.checked } })} />
              First entry only
            </label>
            <HelpTip entry={STRATEGY_HELP.tradeOnlyFirstEntry} />
          </div>
        )}
      </div>
    );
  };

  const ReEntryCell = (leg: Leg, i: number) => {
    const re = leg.reEntry;
    const slValid = isValidStopLoss(leg.stopLoss);
    if (!re?.enabled || !slValid) {
      return <AddCell label="Re-Entry" disabled={!slValid} title={!slValid ? 'Requires a valid Stop Loss' : undefined}
        onClick={() => onUpdateLeg(i, { reEntry: { ...re, enabled: true } })} />;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400">
            Re-Entry
            <HelpTip entry={reEntryHelp(re.type)} />
          </span>
          <RemoveBtn onClick={() => onUpdateLeg(i, { reEntry: { ...re, enabled: false } })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={re.type} options={RE_ENTRY_OPTS}
          onChange={(v) => onUpdateLeg(i, { reEntry: { ...re, type: v as any } })} />
        <Dropdown size="sm" portal className="w-full" value={String(re.count || 1)} options={COUNT_OPTIONS}
          onChange={(v) => onUpdateLeg(i, { reEntry: { ...re, count: parseInt(v) } })} />
      </div>
    );
  };

  const ReExecCell = (leg: Leg, i: number) => {
    const rex = leg.reExecute;
    const tpValid = isValidTargetProfit(leg.targetProfit);
    if (!rex?.enabled || !tpValid) {
      return <AddCell label="Re-Exec" disabled={!tpValid} title={!tpValid ? 'Requires a valid Target Profit' : undefined}
        onClick={() => onUpdateLeg(i, { reExecute: { ...rex, enabled: true } })} />;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 text-[11px] font-bold text-teal-600 dark:text-teal-400">
            Re-Exec
            <HelpTip entry={STRATEGY_HELP.reExecute} />
          </span>
          <RemoveBtn onClick={() => onUpdateLeg(i, { reExecute: { ...rex, enabled: false } })} />
        </div>
        <Dropdown size="sm" portal className="w-full" value={rex.type} options={RE_EXEC_OPTS}
          onChange={(v) => onUpdateLeg(i, { reExecute: { ...rex, type: v as any } })} />
        <Dropdown size="sm" portal className="w-full" value={String(rex.count || 1)} options={COUNT_OPTIONS}
          onChange={(v) => onUpdateLeg(i, { reExecute: { ...rex, count: parseInt(v) } })} />
      </div>
    );
  };

  // Hedge-only: opt this hedge into the recycle model (square the hedge when its main goes
  // flat; re-buy a fresh hedge per re-entered chunk). Offered when its main has any
  // early-exit-or-re-entry trigger (SL / TSL / TP / re-entry / re-execute) — mirrors backend
  // validate_hedge_legs. Returns null when the main is ineligible, so callers render nothing.
  // Stored on the hedge leg; auto-cleared when the main becomes ineligible
  // (usePositionManagement) and dropped with the hedge on remove.
  const RecycleCell = (leg: Leg, i: number) => {
    if (!mainRecycleEligible(legs.find((l) => l.id === leg.hedgeForLegId))) return null;
    return (
      <div className="flex items-start gap-1 pt-1 text-[10px] text-gray-600 dark:text-gray-300">
        <label className="flex items-start gap-1 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={!!leg.hedgeRecycle}
            onChange={(e) => onUpdateLeg(i, { hedgeRecycle: e.target.checked })} />
          <span className="font-medium text-teal-700 dark:text-teal-300">Recycle</span>
        </label>
        <HelpTip entry={STRATEGY_HELP.hedgeRecycle} />
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* per-feature column toggles */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-gray-400 dark:text-gray-500 mr-1">Features:</span>
        {ADV_COLS.map((c) => (
          <button key={c.key} type="button" onClick={() => toggleCol(c.key)}
            className={cn(
              'px-2 py-0.5 text-[11px] font-medium rounded-full border transition-colors',
              visible[c.key] ? BRAND_CHIP_ON : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-gray-400',
            )}>
            {visible[c.key] ? '✓ ' : '+ '}{c.label}
          </button>
        ))}
      </div>

      {/* desktop / wide: aligned table (lg+) */}
      <div data-testid="leg-matrix-table" className="hidden lg:block rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="min-w-max">
          {/* header */}
          <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <HCol w="w-7">#</HCol>
            <HCol w="w-28" entry={STRATEGY_HELP.strikeSelection}>Method</HCol>
            <HCol w="w-40" entry={STRATEGY_HELP.strikeSelection}>Strike/Premium</HCol>
            <HCol w="w-16" entry={STRATEGY_HELP.optionType}>Type</HCol>
            <HCol w="w-16" entry={STRATEGY_HELP.action}>Action</HCol>
            <HCol w="w-16" entry={STRATEGY_HELP.totalLots}>Lots</HCol>
            <HCol w="w-28" entry={STRATEGY_HELP.stopLoss}>Stop Loss</HCol>
            <HCol w="w-28" entry={STRATEGY_HELP.targetProfit}>Target</HCol>
            {visible.trailSL && <HCol w="w-28" entry={STRATEGY_HELP.trailingStopLoss}>Trail SL</HCol>}
            {visible.wat && <HCol w="w-28" entry={STRATEGY_HELP.waitAndTrade}>Wait &amp; Trade</HCol>}
            {visible.reEntry && <HCol w="w-36" entry={STRATEGY_HELP.reEntry}>SL Re-Entry</HCol>}
            {visible.reExec && <HCol w="w-32" entry={STRATEGY_HELP.reExecute}>TP Re-Exec</HCol>}
            <HCol w="w-20" align="right">·</HCol>
          </div>

          {/* rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {legs.map((leg, i) => {
              const hedgeRow = !!leg.isHedge;
              // Hedge legs carry no risk features — render those cells as a muted dash.
              const riskDash = <div className="pt-1.5 text-center text-xs text-gray-300 dark:text-gray-600">—</div>;
              return (
              <div key={leg.id} className={cn('flex items-start gap-3 px-3 py-3',
                hedgeRow ? 'bg-teal-50/40 dark:bg-teal-900/10 border-l-2 border-teal-400 dark:border-teal-500'
                         : 'bg-white dark:bg-gray-800/60')}>
                <span className="w-7 shrink-0 pt-1.5 text-xs font-bold text-gray-400">{hedgeRow ? '↳' : mainNoById.get(leg.id)}</span>
                <div className="w-28 shrink-0 pt-0.5">
                  {hedgeRow && (
                    <>
                      <span className={cn('mb-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase', CHIP_DENSE.HEDGE)}
                        title={`Protective hedge for ${hedgeTargetLabel(leg)}`}>
                        <Shield className="h-2.5 w-2.5" /> Hedge {hedgeTargetLabel(leg)}
                      </span>
                      {/* The hedge's only config option (Recycle) lives under the chip — a
                          stable home always rendered for a hedge, so it's reachable no matter
                          which risk-feature columns are visible. Self-gates on main eligibility. */}
                      {RecycleCell(leg, i)}
                    </>
                  )}
                  {StrikeMethod(leg, i)}
                </div>
                <div className="w-40 shrink-0 pt-0.5">{StrikeValue(leg, i)}</div>
                <div className="w-16 shrink-0 pt-0.5">{OptionTypeToggle(leg, i)}</div>
                <div className="w-16 shrink-0 pt-0.5">{ActionToggle(leg, i)}</div>
                <div className="w-16 shrink-0 pt-0.5">{LotsInput(leg, i)}</div>
                <div className="w-28 shrink-0">{hedgeRow ? riskDash : SLCell(leg, i)}</div>
                <div className="w-28 shrink-0">{hedgeRow ? riskDash : TargetCell(leg, i)}</div>
                {visible.trailSL && <div className="w-28 shrink-0">{hedgeRow ? riskDash : TrailCell(leg, i)}</div>}
                {visible.wat && <div className="w-28 shrink-0">{hedgeRow ? riskDash : WatCell(leg, i)}</div>}
                {/* Hedge rows carry no re-entry/re-exec — their Recycle toggle lives under
                    the hedge chip in the strike column (always reachable). */}
                {visible.reEntry && <div className="w-36 shrink-0">{hedgeRow ? riskDash : ReEntryCell(leg, i)}</div>}
                {visible.reExec && <div className="w-32 shrink-0">{hedgeRow ? riskDash : ReExecCell(leg, i)}</div>}
                <div className="w-20 shrink-0 pt-0.5 flex items-center justify-end gap-1">
                  {!hedgeRow && onAddHedge && leg.actionType === 'SELL' && !mainHasHedge(leg.id) && (
                    <button type="button" onClick={() => onAddHedge(i)} title="Add protective hedge"
                      className="p-1 rounded text-gray-400 hover:text-indigo-600">
                      <Shield className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {!hedgeRow && onCopyLeg && (
                    <button type="button" onClick={() => onCopyLeg(i)} title="Duplicate leg"
                      className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {onRemoveLeg && (hedgeRow || mainCount > 1) && (
                    <button type="button" onClick={() => onRemoveLeg(i)} title={hedgeRow ? 'Remove hedge' : 'Delete leg'}
                      className="p-1 rounded text-gray-400 hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* mobile / narrow: stacked per-leg cards (<lg) */}
      <div className="lg:hidden space-y-3">
        {legs.map((leg, i) => {
          const hedgeRow = !!leg.isHedge;
          return (
          <div key={leg.id} className={cn('rounded-lg border p-3 space-y-3',
            hedgeRow ? 'border-teal-300 dark:border-teal-700 bg-teal-50/40 dark:bg-teal-900/10'
                     : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60')}>
            <div className="flex items-center justify-between">
              {hedgeRow ? (
                <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', CHIP_DENSE.HEDGE)}
                  title={`Protective hedge for ${hedgeTargetLabel(leg)}`}>
                  <Shield className="h-3 w-3" /> Hedge → {hedgeTargetLabel(leg)}
                </span>
              ) : (
                <span className="text-xs font-bold text-gray-500 dark:text-gray-400">Leg {mainNoById.get(leg.id)}</span>
              )}
              <div className="flex items-center gap-1">
                {!hedgeRow && onAddHedge && leg.actionType === 'SELL' && !mainHasHedge(leg.id) && (
                  <button type="button" onClick={() => onAddHedge(i)} title="Add protective hedge"
                    className="p-1 rounded text-gray-400 hover:text-indigo-600">
                    <Shield className="h-4 w-4" />
                  </button>
                )}
                {!hedgeRow && onCopyLeg && (
                  <button type="button" onClick={() => onCopyLeg(i)} title="Duplicate leg"
                    className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                    <Copy className="h-4 w-4" />
                  </button>
                )}
                {onRemoveLeg && (hedgeRow || mainCount > 1) && (
                  <button type="button" onClick={() => onRemoveLeg(i)} title={hedgeRow ? 'Remove hedge' : 'Delete leg'}
                    className="p-1 rounded text-gray-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MobileField label="Method" entry={STRATEGY_HELP.strikeSelection}>{StrikeMethod(leg, i)}</MobileField>
              <MobileField label="Strike/Premium" entry={STRATEGY_HELP.strikeSelection}>{StrikeValue(leg, i)}</MobileField>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <MobileField label="Type" entry={STRATEGY_HELP.optionType}>{OptionTypeToggle(leg, i)}</MobileField>
              <MobileField label="Action" entry={STRATEGY_HELP.action}>{ActionToggle(leg, i)}</MobileField>
              <MobileField label="Lots" entry={STRATEGY_HELP.totalLots}>{LotsInput(leg, i)}</MobileField>
            </div>
            {/* Hedge legs carry no risk features — the risk grid is omitted; the only
                hedge-configurable option is Recycle (shown when its main is recycle-eligible). */}
            {!hedgeRow ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                <div>{SLCell(leg, i)}</div>
                <div>{TargetCell(leg, i)}</div>
                {visible.trailSL && <div>{TrailCell(leg, i)}</div>}
                {visible.wat && <div>{WatCell(leg, i)}</div>}
                {visible.reEntry && <div>{ReEntryCell(leg, i)}</div>}
                {visible.reExec && <div>{ReExecCell(leg, i)}</div>}
              </div>
            ) : (
              // RecycleCell self-gates (returns null unless the main is recycle-eligible).
              RecycleCell(leg, i)
            )}
          </div>
          );
        })}
      </div>

      {onAddLeg && (
        <button type="button" onClick={onAddLeg}
          className={cn('inline-flex items-center gap-1.5 text-sm font-medium', wl ? 'text-zebu-600 hover:text-zebu-700' : 'text-blue-600 hover:text-blue-700')}>
          <Plus className="h-4 w-4" /> Add Leg
        </button>
      )}

      {tslDialogLeg !== null && legs[tslDialogLeg] && (
        <TrailingStopLossDialog
          initial={legs[tslDialogLeg].trailingStopLoss}
          ulStopLoss={UL_RISK_TYPES.includes(legs[tslDialogLeg].stopLoss?.type)}
          onCancel={() => setTslDialogLeg(null)}
          onSave={(v) => {
            onUpdateLeg(tslDialogLeg, { trailingStopLoss: { ...v, enabled: true } });
            setTslDialogLeg(null);
          }}
        />
      )}
    </div>
  );
};

export default LegMatrix;
